// Keyboard access for scroll areas (axe scrollable-region-focusable: #652, #656,
// #674–#677). Product-card galleries (#649) sit inside the card's link and are
// deliberately left alone; that finding is suppressed in scripts/a11y-scan/config.js.
//
// Arrow keys only scroll a region once focus is inside it, so a carousel or
// table whose content has nothing focusable is out of reach from the keyboard.
// Such a region becomes a tab stop while, and only while, it actually scrolls:
// most .scroll-area elements are carousels on mobile and plain grids on
// desktop, where a tab stop would lead nowhere. Mouse and touch users see no
// change; theme.css hides the focus ring unless it is :focus-visible.
//
// Kept out of theme.js on purpose: a failure here must not take the theme's
// module graph down with it.

// `.scroll-area` is the theme's class for every horizontal carousel/row.
// `.table-wrapper` is the overflow box around the Trade page's comparison
// table, in a custom-html block edited in the theme editor: renaming that class
// there drops this fix, and the scan reopens #677 as a regression.
const SCROLL_REGION_SELECTOR = '.scroll-area, .table-wrapper';

const FOCUSABLE =
    'a[href], area[href], button, input:not([type="hidden"]), select, textarea, iframe, summary, audio[controls], video[controls], [contenteditable]:not([contenteditable="false"]), [tabindex]';

// axe's scrollable-region-focusable counts an axis as scrolling only past this
// many pixels of overflow, and only if that axis's overflow is auto or scroll.
// Matching it adds a tab stop exactly where axe asks for one.
const OVERFLOW_BUFFER_PX = 13;

// The regions whose tabindex this module set, so it only ever removes its own.
const managed = new WeakSet();

function scrolls(element) {
    const style = getComputedStyle(element);
    const canScroll = (axis) =>
        ['auto', 'scroll'].includes(style.getPropertyValue(`overflow-${axis}`));
    return (
        (canScroll('x') && element.scrollWidth > element.clientWidth + OVERFLOW_BUFFER_PX) ||
        (canScroll('y') && element.scrollHeight > element.clientHeight + OVERFLOW_BUFFER_PX)
    );
}

function canTakeFocus(element) {
    if (element.matches(':disabled') || element.tabIndex < 0 || element.closest('[hidden]'))
        return false;
    return element.checkVisibility ? element.checkVisibility({ visibilityProperty: true }) : true;
}

function hasFocusableContent(element) {
    return Array.from(element.querySelectorAll(FOCUSABLE)).some(canTakeFocus);
}

/**
 * Give `element` tabindex="0" if it scrolls, has nothing focusable inside and
 * is not itself inside a link or button (a product card's image gallery sits
 * in the card's link, where a tab stop would nest interactive controls).
 * Removes the tabindex again, if this set it, once that stops being true.
 * A tabindex set anywhere else is left alone.
 *
 * @param {HTMLElement} element
 */
export function syncScrollAreaFocus(element) {
    // Another script changed the tabindex this set: from here on it's theirs
    if (managed.has(element) && element.getAttribute('tabindex') !== '0') managed.delete(element);
    if (element.hasAttribute('tabindex') && !managed.has(element)) return;

    const needsTabStop =
        scrolls(element) &&
        !element.parentElement?.closest('a[href], button') &&
        !hasFocusableContent(element);

    if (needsTabStop) {
        // Only write on a change: the attribute observer below sees this write,
        // and the resync it triggers must stop here

        if (!managed.has(element)) element.setAttribute('tabindex', '0');
        managed.add(element);
    } else if (managed.has(element)) {
        element.removeAttribute('tabindex');
        managed.delete(element);
    }
}

if ('ResizeObserver' in window && 'MutationObserver' in window) {
    const resizeObserver = new ResizeObserver((entries) =>
        entries.forEach(({ target }) => syncScrollAreaFocus(target)),
    );
    const regionsIn = (root) => [
        ...(root.matches?.(SCROLL_REGION_SELECTOR) ? [root] : []),
        ...root.querySelectorAll(SCROLL_REGION_SELECTOR),
    ];

    regionsIn(document).forEach((region) => resizeObserver.observe(region));
    // Sections re-rendered over AJAX (facets, cart drawer, theme editor) add and
    // remove scroll areas, and can change what an existing one contains. The
    // attributes are the ones that decide whether content can take focus.
    new MutationObserver((records) =>
        records.forEach((record) => {
            record.removedNodes.forEach(
                (node) =>
                    node.nodeType === Node.ELEMENT_NODE &&
                    regionsIn(node).forEach((region) => resizeObserver.unobserve(region)),
            );
            record.addedNodes.forEach(
                (node) =>
                    node.nodeType === Node.ELEMENT_NODE &&
                    regionsIn(node).forEach((region) => resizeObserver.observe(region)),
            );
            const enclosing = record.target.closest?.(SCROLL_REGION_SELECTOR);
            if (enclosing) syncScrollAreaFocus(enclosing);
        }),
    ).observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['disabled', 'hidden', 'tabindex', 'href'],
    });
    // Images loading can widen a region's content without resizing the region
    // itself; lazy ones keep loading after the window's load event
    window.addEventListener('load', () => regionsIn(document).forEach(syncScrollAreaFocus));
    document.addEventListener(
        'load',
        ({ target }) => {
            const region =
                target instanceof HTMLImageElement && target.closest(SCROLL_REGION_SELECTOR);
            if (region) syncScrollAreaFocus(region);
        },
        true,
    );
}
